import React, { useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import contactEmailScreenshot from '../../../../assets/img_contact_email_image.jpg';
import privacyPolicyScreenshot from '../../../../assets/img_privacy_policy_image.jpg';
import termOfServiceScreenshot from '../../../../assets/img_term_of_service_image.jpg';
import aboutUsScreenshot from '../../../../assets/img_about_us_image.jpg';
import complaintsScreenshot from '../../../../assets/img_complaints_image.jpg';
import informedConsentScreenshot from '../../../../assets/img_informed_consent_image.jpg';
import { Skeleton } from 'primereact/skeleton';
import useTenantStore from '../../../../stores/TenantStore';
import OpenAPI from '../../../../endpoint/Openapi';
import { normalizeString } from '../../../../utils/GeneralUtils';
import { type CustomError } from '../../../../models/Error';

function Links() {
  const { id: tenantId, featureSettings, setTenantConfig } = useTenantStore();
  const [contactEmail, setContactEmail] = React.useState(() => '');
  const [privacyPolicy, setPrivacyPolicy] = React.useState(() => '');
  const [termsOfService, setTermsOfService] = React.useState(() => '');
  const [aboutUs, setAboutUs] = React.useState(() => '');
  const [screenshotImage, setScreenshotImage] = React.useState(
    () => contactEmailScreenshot,
  );
  const [complaints, setComplaints] = React.useState(() => '');
  const [informedConsent, setInformedConsent] = React.useState(() => '');
  const [saveLoading, setSaveLoading] = React.useState(() => false);
  const toast = React.useRef<Toast>(null);

  useEffect(() => {
    setContactEmail(featureSettings?.linkSettings?.contactEmail ?? '');
    setPrivacyPolicy(featureSettings?.linkSettings?.privacyPolicyLink ?? '');
    setTermsOfService(featureSettings?.linkSettings?.termsOfServiceLink ?? '');
    setAboutUs(featureSettings?.linkSettings?.aboutUsLink ?? '');
    setComplaints(featureSettings?.linkSettings?.complaintLink ?? '');
    setInformedConsent(featureSettings?.linkSettings?.informedConsent ?? '');
  }, [featureSettings?.linkSettings]);

  async function saveData() {
    setSaveLoading(true);

    try {
      const res = await OpenAPI.POST('/api/v2/admin/feature-setting', {
        params: {
          query: {
            tenantId,
          },
        },
        body: {
          linkSettings: {
            contactEmail,
            privacyPolicyLink: privacyPolicy,
            termsOfServiceLink: termsOfService,
            aboutUsLink: aboutUs,
            complaintLink: normalizeString(complaints),
            informedConsent: normalizeString(informedConsent),
          },
        },
      });

      // @ts-expect-error
      if (res.error) throw res.error;

      try {
        const { data } = await OpenAPI.GET('/api/v2/admin/tenant');
        if (data) setTenantConfig(data);
      } catch {}

      toast.current?.show({
        severity: 'success',
        summary: `Saved successfully`,
        life: 3000,
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setSaveLoading(false);
  }

  return (
    <div>
      {saveLoading ? (
        <Skeleton width="100rem" height="100rem" />
      ) : (
        <div>
          <div className="gap-4">
            <div className="flex flex-row w-full items-start gap-4 max-md:flex-col max-md:gap-8 max-md:items-center">
              <div className="flex flex-col mr-4 w-[50%] max-xl:w-[90%] max-w-[512px] bg-zinc-700 rounded-3xl max-md:mr-0 max-md:w-full max-md:max-w-[320px]">
                <img src={screenshotImage} />
              </div>

              <div className="flex flex-col justify-center w-full px-6 py-8 rounded-3xl shadow-[0px_4px_12px_rgba(0,0,0,0.1)] text-base leading-6 text-black bg-white">
                <div className="text-3xl font-medium">Links</div>

                <div className="flex flex-row flex-wrap w-full gap-x-4 gap-y-6">
                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Contact Email</div>
                    <InputText
                      value={contactEmail}
                      onChange={(e) => {
                        setContactEmail(e.target.value);
                      }}
                      onFocus={() => {
                        setScreenshotImage(contactEmailScreenshot);
                      }}
                      placeholder="abc@def.com"
                      className="mt-3 p-inputtext-lg w-full"
                    />
                  </div>

                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Privacy policy</div>
                    <InputText
                      value={privacyPolicy}
                      onChange={(e) => {
                        setPrivacyPolicy(e.target.value);
                      }}
                      onFocus={() => {
                        setScreenshotImage(privacyPolicyScreenshot);
                      }}
                      placeholder="https://..."
                      className="mt-3 p-inputtext-lg w-full"
                    />
                  </div>

                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Terms of service</div>
                    <InputText
                      value={termsOfService}
                      onChange={(e) => {
                        setTermsOfService(e.target.value);
                      }}
                      onFocus={() => {
                        setScreenshotImage(termOfServiceScreenshot);
                      }}
                      placeholder="https://..."
                      className="mt-3 p-inputtext-lg w-full"
                    />
                  </div>

                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>About Us</div>
                    <InputText
                      value={aboutUs}
                      onChange={(e) => {
                        setAboutUs(e.target.value);
                      }}
                      onFocus={() => {
                        setScreenshotImage(aboutUsScreenshot);
                      }}
                      placeholder="https://..."
                      className="mt-3 p-inputtext-lg w-full"
                    />
                  </div>

                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Complaints</div>
                    <InputText
                      value={complaints}
                      onChange={(e) => {
                        setComplaints(e.target.value);
                      }}
                      onFocus={() => {
                        setScreenshotImage(complaintsScreenshot);
                      }}
                      placeholder="https://..."
                      className="mt-3 p-inputtext-lg w-full"
                    />
                  </div>

                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Informed consent</div>
                    <InputText
                      value={informedConsent}
                      onChange={(e) => {
                        setInformedConsent(e.target.value);
                      }}
                      onFocus={() => {
                        setScreenshotImage(informedConsentScreenshot);
                      }}
                      placeholder="https://..."
                      className="mt-3 p-inputtext-lg w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-row w-full px-6 py-6 justify-end">
            <Button
              label="Save"
              className={`text-2xl px-14 py-3 bg-[#F1B51C] hover:bg-bluegray-400 text-black border-none`}
              onClick={saveData}
            />
          </div>
        </div>
      )}

      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}

export default Links;
